import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { memo, useCallback, useMemo, useState } from 'react';
import rafSchedule from 'raf-schd';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { handleNavigation } from '@atlaskit/editor-common/link';
import { findOverflowScrollParent, UnsupportedInline } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { registerCard } from '../pm-plugins/actions';
import OverlayWrapper from '../ui/ConfigureOverlay';
import { getAwarenessProps } from '../utils';
import { Card } from './genericCard';
import { InlineCardWithAwareness } from './inlineCardWithAwareness';
export var InlineCard = /*#__PURE__*/memo(function (_ref) {
  var node = _ref.node,
    cardContext = _ref.cardContext,
    actionOptions = _ref.actionOptions,
    showServerActions = _ref.showServerActions,
    useAlternativePreloader = _ref.useAlternativePreloader,
    view = _ref.view,
    getPos = _ref.getPos,
    onClick = _ref.onClick,
    onRes = _ref.onResolve,
    isHovered = _ref.isHovered,
    showHoverPreview = _ref.showHoverPreview,
    hoverPreviewOptions = _ref.hoverPreviewOptions;
  var _node$attrs = node.attrs,
    url = _node$attrs.url,
    data = _node$attrs.data;
  var scrollContainer = useMemo(function () {
    return findOverflowScrollParent(view.dom) || undefined;
  }, [view.dom]);
  var onResolve = useCallback(function (data) {
    if (!getPos || typeof getPos === 'boolean') {
      return;
    }
    var title = data.title,
      url = data.url;
    // don't dispatch immediately since we might be in the middle of
    // rendering a nodeview
    rafSchedule(function () {
      // prosemirror-bump-fix
      var pos = getPos();
      if (typeof pos !== 'number') {
        return;
      }
      var tr = view.state.tr;
      registerCard({
        title: title,
        url: url,
        pos: pos
      })(tr);
      onRes === null || onRes === void 0 || onRes(tr, title);
      view.dispatch(tr);
    })();
  }, [getPos, view, onRes]);
  var onError = useCallback(function (data) {
    var url = data.url,
      err = data.err;
    if (err) {
      throw err;
    }
    onResolve({
      url: url
    });
  }, [onResolve]);
  var card = useMemo(function () {
    return /*#__PURE__*/React.createElement(SmartCard, {
      key: url,
      url: url,
      data: data,
      appearance: "inline",
      onClick: onClick,
      container: scrollContainer,
      onResolve: onResolve,
      onError: onError,
      inlinePreloaderStyle: useAlternativePreloader ? 'on-right-without-skeleton' : undefined,
      actionOptions: actionOptions,
      showServerActions: showServerActions,
      isHovered: isHovered,
      showHoverPreview: showHoverPreview,
      hoverPreviewOptions: hoverPreviewOptions
    });
  }, [url, data, onClick, scrollContainer, onResolve, onError, useAlternativePreloader, actionOptions, showServerActions, isHovered, showHoverPreview, hoverPreviewOptions]);

  // [WS-2307]: we only render card wrapped into a Provider when the value is ready,
  // otherwise if we got data, we can render the card directly since it doesn't need the Provider
  return cardContext && cardContext.value ? /*#__PURE__*/React.createElement(cardContext.Provider, {
    value: cardContext.value
  }, card) : data ? card : null;
});
var WrappedInlineCardWithAwareness = Card(InlineCardWithAwareness, UnsupportedInline);
var WrappedInlineCard = Card(InlineCard, UnsupportedInline);
export function InlineCardNodeView(props) {
  var _floatingToolbarState;
  var useAlternativePreloader = props.useAlternativePreloader,
    node = props.node,
    view = props.view,
    getPos = props.getPos,
    actionOptions = props.actionOptions,
    showServerActions = props.showServerActions,
    allowEmbeds = props.allowEmbeds,
    allowBlockCards = props.allowBlockCards,
    enableInlineUpgradeFeatures = props.enableInlineUpgradeFeatures,
    pluginInjectionApi = props.pluginInjectionApi,
    onClickCallback = props.onClickCallback,
    __livePage = props.__livePage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOverlayHovered = _useState2[0],
    setIsOverlayHovered = _useState2[1];
  var _useSharedPluginState = useSharedPluginState(pluginInjectionApi, ['floatingToolbar', 'editorViewMode']),
    editorViewModeState = _useSharedPluginState.editorViewModeState,
    floatingToolbarState = _useSharedPluginState.floatingToolbarState;
  var floatingToolbarNode = floatingToolbarState === null || floatingToolbarState === void 0 || (_floatingToolbarState = floatingToolbarState.configWithNodeInfo) === null || _floatingToolbarState === void 0 ? void 0 : _floatingToolbarState.node;
  if (__livePage && fg('platform.linking-platform.smart-links-in-live-pages')) {
    var showHoverPreview = floatingToolbarNode !== node;
    var livePagesHoverCardFadeInDelay = 800;
    var inlineCard = /*#__PURE__*/React.createElement(WrappedInlineCard, {
      isHovered: isOverlayHovered,
      node: node,
      view: view,
      getPos: getPos,
      actionOptions: actionOptions,
      showServerActions: showServerActions,
      useAlternativePreloader: useAlternativePreloader,
      onClickCallback: onClickCallback,
      showHoverPreview: showHoverPreview,
      hoverPreviewOptions: {
        fadeInDelay: livePagesHoverCardFadeInDelay
      }
    });
    return (editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode) === 'view' ? inlineCard : /*#__PURE__*/React.createElement(OverlayWrapper, {
      targetElementPos: getPos(),
      view: view,
      isHoveredCallback: setIsOverlayHovered,
      onOpenLinkClick: function onOpenLinkClick(event) {
        var _pluginInjectionApi$a;
        handleNavigation({
          fireAnalyticsEvent: pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions.fireAnalyticsEvent,
          onClickCallback: onClickCallback,
          url: node.attrs.url,
          event: event
        });
      }
    }, inlineCard);
  }
  return /*#__PURE__*/React.createElement(WrappedInlineCardWithAwareness, _extends({
    node: node,
    view: view,
    getPos: getPos,
    actionOptions: actionOptions,
    showServerActions: showServerActions,
    useAlternativePreloader: useAlternativePreloader,
    pluginInjectionApi: pluginInjectionApi,
    onClickCallback: onClickCallback
  }, enableInlineUpgradeFeatures && getAwarenessProps(view.state, getPos, allowEmbeds, allowBlockCards)));
}